import { useId, useLayoutEffect, useRef } from 'react';
import { Callout } from '@carbon/react';
import { FOCUSABLE_SELECTOR } from 'utils/focus';

import { useDatasetConfig } from './dataset-context';
import styles from './facets-failed-notice.module.scss';

interface NoticeProps {
  retrying: boolean;
  onRetry: () => void;
  handOffFocus?: boolean;
  className?: string;
}

/**
 * What a dataset's failed counts say.
 * @param {string} label The dataset's label.
 * @return {string} The message.
 */
const failedMessage = (label: string) => `${label} filter counts couldn't load`;

/**
 * Announces a dataset's failed filter counts. Mounted empty before they fail, and
 * empty again while a Retry press runs, so a failed retry is said again; other
 * refetches leave it be, so one outage isn't said on every pan.
 * @param {object} props The status.
 * @param {boolean} props.failed Whether the counts failed to load.
 * @param {boolean} props.retrying Whether a Retry press waits on its request.
 * @return {JSX.Element} The visually hidden status region.
 */
export function FacetsFailedStatus({
  failed,
  retrying,
}: {
  failed: boolean;
  retrying: boolean;
}): JSX.Element {
  const { label } = useDatasetConfig();
  return (
    <div role="status" className={styles.visuallyHidden}>
      {failed && !retrying && failedMessage(label)}
    </div>
  );
}

/**
 * Shows a dataset's failed filter counts with a Retry. Carbon's Callout, so it
 * neither traps Tab nor closes on Escape; it stays up while the retry runs.
 * FacetsFailedStatus announces it.
 * @param {object} props The notice.
 * @param {boolean} props.failed Whether the counts failed to load.
 * @param {boolean} props.retrying Whether a Retry press waits on its request.
 * @param {Function} props.onRetry Handles a press of Retry.
 * @param {boolean} [props.handOffFocus] Whether focus goes to the next control
 * when the notice goes; off while its container closes, so focus stays out.
 * @param {string} [props.className] Wraps the notice.
 * @return {JSX.Element | null} The notice, while failed.
 */
export function FacetsFailedNotice({
  failed,
  ...notice
}: NoticeProps & { failed: boolean }): JSX.Element | null {
  return failed ? <Notice {...notice} /> : null;
}

/**
 * The shown notice, handing focus on as it goes.
 * @param {object} props The notice.
 * @return {JSX.Element} The notice.
 */
function Notice({
  retrying,
  onRetry,
  handOffFocus = true,
  className,
}: NoticeProps): JSX.Element {
  const { label } = useDatasetConfig();
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const handOff = useRef(handOffFocus);
  useLayoutEffect(() => {
    handOff.current = handOffFocus;
  });
  // Counts landing removes the notice, often with focus on its button; hand
  // focus to the next control so a keyboard user isn't dropped to the page.
  useLayoutEffect(() => {
    const notice = ref.current;
    return () => {
      if (!handOff.current || !notice?.contains(document.activeElement)) return;
      Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .find(
          (el) =>
            !notice.contains(el) &&
            notice.compareDocumentPosition(el) &
              Node.DOCUMENT_POSITION_FOLLOWING
        )
        ?.focus();
    };
  }, []);

  return (
    <div ref={ref} className={className}>
      <Callout
        kind="warning"
        lowContrast
        title={failedMessage(label)}
        titleId={titleId}
        actionButtonLabel={retrying ? 'Retrying...' : 'Retry'}
        onActionButtonClick={onRetry}
      />
    </div>
  );
}
